/**
 * WHO HOLDS IT (migration 0034) — an engagement or a piece of work, given to
 * a member of the team, or to nobody.
 *
 * One quiet line: who holds it, and a way to change it. The list of people is
 * read when a person asks to change it, never before; the holder shown is the
 * server's answer to the act, not this screen's guess.
 */
import { useState } from "react";
import { AssignmentResponse, TeamResponse } from "@leveri/contracts";
import { Button, Notice } from "../file/index.js";
import { call, failureMessage } from "../api.js";

export interface Holder { memberId: string; userId: string; displayName: string }

export function AssignControl({ objectKind, objectId, holder, onChanged }: {
  objectKind: "ENGAGEMENT" | "WORK";
  objectId: string;
  holder: Holder | null;
  onChanged: (next: Holder | null) => void;
}) {
  const [people, setPeople] = useState<TeamResponse["members"] | null>(null);
  const [choosing, setChoosing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  async function open() {
    setChoosing(true);
    setFailure(null);
    try { setPeople((await call("GET", "/api/firm/team", TeamResponse)).members); } catch (error) { setFailure(failureMessage(error)); }
  }
  async function assign(memberId: string | null) {
    setSaving(true);
    setFailure(null);
    try {
      const r = await call("POST", "/api/assignments", AssignmentResponse, { objectKind, objectId, assigneeMemberId: memberId });
      onChanged(r.assignment ? { memberId: r.assignment.memberId, userId: r.assignment.userId, displayName: r.assignment.displayName } : null);
      setChoosing(false);
    } catch (error) {
      setFailure(failureMessage(error));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="text-[13px]" data-testid={`holder-${objectKind.toLowerCase()}`}>
      {!choosing && (
        <span className="inline-flex flex-wrap items-baseline gap-x-2.5">
          <span className={holder ? "text-ink-soft" : "text-stone"}>{holder ? `Held by ${holder.displayName}` : "Nobody holds this yet"}</span>
          <Button variant="ghost" size="sm" className="h-auto px-0" onClick={() => void open()} data-testid="assign-open">{holder ? "Change" : "Assign"}</Button>
        </span>
      )}
      {choosing && (
        <span className="inline-flex flex-wrap items-center gap-2">
          <label className="sr-only" htmlFor={`assign-${objectId}`}>Give it to</label>
          <select
            id={`assign-${objectId}`}
            disabled={!people || saving}
            defaultValue={holder?.memberId ?? ""}
            onChange={(e) => void assign(e.target.value === "" ? null : e.target.value)}
            className="h-8 rounded-control border border-line-strong bg-canvas px-2 text-[13px] text-ink focus:border-brand"
            data-testid="assign-select"
          >
            {!people && <option value={holder?.memberId ?? ""}>Reading the team…</option>}
            {people && <option value="">Nobody</option>}
            {people?.map((p) => <option key={p.memberId} value={p.memberId}>{p.displayName}{p.you ? " (you)" : ""}</option>)}
          </select>
          <Button variant="ghost" size="sm" className="h-auto px-0" disabled={saving} onClick={() => setChoosing(false)}>Cancel</Button>
        </span>
      )}
      {failure && <div className="mt-1"><Notice>{failure}</Notice></div>}
    </div>
  );
}
